import { GetTimesheetsResponse } from '@sit/openapi';
import { useGetCurrentCompany } from '@web/api/company/use-get-company';
import { useSendPrintToIntacct } from '@web/api/timesheet/use-print-timesheet';
import { useLocationQuery } from '@web/helpers/hooks';
import { useIsTopLevelEntity } from '@web/hooks/use-current-entity';
import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useTimesheetModalActions } from '.';
import { Actions } from '../components/TimesheetsTable/TableRowActions';
import { useOvertimeSubmitGuard } from './useOvertimeSubmitGuard';

export const useTimesheetsPage = () => {
  const query = useLocationQuery();
  const { openNewTimesheetModal, openDuplicateTimesheetModal, openBulkDeleteTimesheetsModal } = useTimesheetModalActions();
  const navigate = useNavigate();
  const sendPrintToIntacct = useSendPrintToIntacct();
  const { data: company } = useGetCurrentCompany();
  const isTopLevel = useIsTopLevelEntity();
  const { requestSubmit, overtimeModal } = useOvertimeSubmitGuard();

  const openCreateTimesheetModal = query.get('create_new_timesheet') === 'true';
  const managedAtEntityLevel = !!company?.managedAtEntityLevel;

  const disableCreateTimesheet = isTopLevel && managedAtEntityLevel;

  useEffect(() => {
    if (!managedAtEntityLevel && openCreateTimesheetModal) {
      openNewTimesheetModal();
      void navigate({ search: '' }, { replace: true });
    }
  }, [managedAtEntityLevel, navigate, openCreateTimesheetModal, openNewTimesheetModal]);

  const handleAction = useCallback(
    (
      action: Actions,
      timesheet: Pick<
        GetTimesheetsResponse[number],
        'id' | 'employeeId' | 'startDate' | 'weekStart' | 'companyId' | 'description' | 'endDate'
      >,
    ) => {
      switch (action) {
        case 'submit':
          requestSubmit(timesheet);
          break;
        case 'duplicate':
          openDuplicateTimesheetModal(timesheet.id);
          break;
        case 'print-to-intacct':
          sendPrintToIntacct({ timesheetId: timesheet.id });
          break;
        case 'delete':
          openBulkDeleteTimesheetsModal([timesheet]);
          break;
      }
    },
    [openBulkDeleteTimesheetsModal, openDuplicateTimesheetModal, requestSubmit, sendPrintToIntacct],
  );

  return {
    managedAtEntityLevel,
    handleAction,
    disableCreateTimesheet,
    overtimeModal,
  };
};
